/**
 * Phase 27.50.C — Admin Storefront Manager (`/admin/storefront`).
 *
 * Three tabs:
 *   1. Products — bulk publish/unpublish/archive with checkbox selection.
 *   2. Coupons — full CRUD ($ or % off, min spend, expiration, max uses, free shipping).
 *   3. Shipping — flat rate + free-shipping-over threshold.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { shopAdminAPI, marketingAPI, agentAPI } from '@/lib/api';
import { getErrorMessage, formatPhoneInput } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Plus, Trash2, Save, Package, Tag, Truck, Pencil, Layers, ExternalLink, ShoppingBag, RefreshCw, Ban, CheckCircle2, X } from 'lucide-react';

const STATUS_TONE = {
  draft:     'bg-slate-100 text-slate-700 border-slate-300',
  published: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  archived:  'bg-rose-50 text-rose-800 border-rose-300',
};

export default function AdminStorefrontPage() {
  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="admin-storefront-page">
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Storefront Manager</h1>
      <Tabs defaultValue="products" className="w-full">
        <TabsList data-testid="admin-storefront-tabs">
          <TabsTrigger value="products" data-testid="tab-products">
            <Package size={13} className="mr-1.5" /> Products
          </TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">
            <ShoppingBag size={13} className="mr-1.5" /> Orders
          </TabsTrigger>
          <TabsTrigger value="coupons" data-testid="tab-coupons">
            <Tag size={13} className="mr-1.5" /> Coupons
          </TabsTrigger>
          <TabsTrigger value="shipping" data-testid="tab-shipping">
            <Truck size={13} className="mr-1.5" /> Shipping
          </TabsTrigger>
        </TabsList>
        <TabsContent value="products"><ProductsTab /></TabsContent>
        <TabsContent value="orders"><OrdersTab /></TabsContent>
        <TabsContent value="coupons"><CouponsTab /></TabsContent>
        <TabsContent value="shipping"><ShippingTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─── Products tab ────────────────────────────────────────────────────

function ProductsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // full template doc

  const load = async () => {
    setLoading(true); setSel(new Set());
    try { setRows((await shopAdminAPI.listProducts()).data.items || []); }
    catch (e) { toast.error(getErrorMessage(e, 'Failed to load products')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    const s = new Set(sel); s.has(id) ? s.delete(id) : s.add(id); setSel(s);
  };
  const toggleAll = () => {
    setSel(sel.size === rows.length ? new Set() : new Set(rows.map((r) => r.template_id)));
  };
  const bulk = async (status) => {
    if (sel.size === 0) return;
    setBusy(true);
    try {
      const r = await shopAdminAPI.bulkStatus({
        template_ids: Array.from(sel), status,
      });
      toast.success(`${r.data.modified} product(s) → ${status}`);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Bulk action failed')); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-6 text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <CardTitle className="text-base">Products ({rows.length})</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={sel.size === 0 || busy}
                  onClick={() => bulk('published')} data-testid="bulk-publish">
            Publish ({sel.size})
          </Button>
          <Button size="sm" variant="outline" disabled={sel.size === 0 || busy}
                  onClick={() => bulk('draft')} data-testid="bulk-unpublish">
            Unpublish
          </Button>
          <Button size="sm" variant="outline" disabled={sel.size === 0 || busy}
                  onClick={() => bulk('archived')} data-testid="bulk-archive">
            Archive
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="admin-products-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 w-8">
                  <Checkbox checked={sel.size === rows.length && rows.length > 0}
                             onCheckedChange={toggleAll} data-testid="select-all" />
                </th>
                <th className="text-left px-3 py-2 font-semibold">Title</th>
                <th className="text-left px-3 py-2 font-semibold">SKU</th>
                <th className="text-left px-3 py-2 font-semibold">Price</th>
                <th className="text-left px-3 py-2 font-semibold">Tiers</th>
                <th className="text-left px-3 py-2 font-semibold">Stock</th>
                <th className="text-left px-3 py-2 font-semibold">Purchasable</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="w-24 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.template_id} className={sel.has(r.template_id) ? 'bg-blue-50/50' : ''}
                    data-testid={`row-${r.template_id}`}>
                  <td className="px-3 py-2">
                    <Checkbox checked={sel.has(r.template_id)}
                               onCheckedChange={() => toggle(r.template_id)}
                               data-testid={`select-${r.template_id}`} />
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-800">{r.title || '—'}</td>
                  <td className="px-3 py-2 font-mono text-slate-500">{r.sku || '—'}</td>
                  <td className="px-3 py-2 font-mono">${Number(r.unit_price || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 font-mono text-slate-600">
                    {(r.pricing_tiers || []).length === 0 ? '—' : `${r.pricing_tiers.length} tier${r.pricing_tiers.length === 1 ? '' : 's'}`}
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.stock_qty ?? '∞'}</td>
                  <td className="px-3 py-2">{r.is_purchasable ? '✓' : '✗'}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[r.status] || STATUS_TONE.draft}`}>
                      {r.status || 'draft'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost"
                            onClick={() => setEditing(r)}
                            title="Quick edit price & tiers"
                            data-testid={`edit-${r.template_id}`}>
                      <Pencil size={12} />
                    </Button>
                    <Button asChild size="sm" variant="ghost"
                            title="Full editor (photos, description, fields)"
                            data-testid={`full-edit-${r.template_id}`}>
                      <Link to={`/marketing/${r.template_id}/edit`}>
                        <ExternalLink size={12} />
                      </Link>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
      <ProductEditor
        product={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </Card>
  );
}

// ─── Product editor (price + tiers) ──────────────────────────────────

function ProductEditor({ product, onClose, onSaved }) {
  const [unitPrice, setUnitPrice] = useState('');
  const [tiers, setTiers] = useState([]);
  const [purchasable, setPurchasable] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setUnitPrice(product.unit_price != null ? String(product.unit_price) : '');
      setTiers((product.pricing_tiers || []).map((t) => ({
        quantity: t.quantity != null ? String(t.quantity) : '',
        price: t.price != null ? String(t.price) : '',
      })));
      setPurchasable(!!product.is_purchasable);
    }
  }, [product]);

  if (!product) return null;

  const addTier = () => setTiers([...tiers, { quantity: '', price: '' }]);
  const removeTier = (idx) => setTiers(tiers.filter((_, i) => i !== idx));
  const updateTier = (idx, field, value) => {
    const next = [...tiers];
    next[idx] = { ...next[idx], [field]: value };
    setTiers(next);
  };

  const handleSave = async () => {
    const parsedTiers = [];
    for (const t of tiers) {
      const q = parseInt(t.quantity, 10);
      const p = parseFloat(t.price);
      if (!Number.isFinite(q) || q <= 0) {
        toast.error('Every tier needs a quantity > 0'); return;
      }
      if (!Number.isFinite(p) || p < 0) {
        toast.error('Every tier needs a valid price'); return;
      }
      parsedTiers.push({ quantity: q, price: p });
    }
    const qtys = parsedTiers.map((t) => t.quantity);
    if (new Set(qtys).size !== qtys.length) {
      toast.error('Tier quantities must be unique'); return;
    }
    parsedTiers.sort((a, b) => a.quantity - b.quantity);

    const payload = { pricing_tiers: parsedTiers };
    const trimmedPrice = unitPrice.toString().trim();
    if (trimmedPrice !== '') {
      const up = parseFloat(trimmedPrice);
      if (!Number.isFinite(up) || up < 0) {
        toast.error('Unit price must be a positive number'); return;
      }
      payload.unit_price = up;
    }
    // Persist the Purchasable toggle exactly as the admin set it. The
    // checkbox is now a one-click publish / unpublish switch, so we
    // must respect an explicit uncheck even when the product has a
    // valid price or tiers.
    const hasValidPrice =
      (payload.unit_price != null && payload.unit_price > 0) ||
      parsedTiers.length > 0;
    payload.is_purchasable = purchasable;
    // UX: "List in shop (publish now)" now really means it. Toggling
    // this checkbox must also flip the template's lifecycle status so
    // the item actually appears (or disappears) in the storefront
    // without the admin having to click Publish separately. Archived
    // items stay archived — that's a permanent state admins manage on
    // the table with the "Archive" bulk button.
    const currentStatus = product?.status || 'draft';
    if (purchasable && hasValidPrice) {
      payload.status = 'published';
    } else if (!purchasable && currentStatus === 'published') {
      payload.status = 'draft';
    }
    setSaving(true);
    try {
      await marketingAPI.updateTemplate(product.template_id, payload);
      // Phase 27.50.C.32 — Give admins an unambiguous confirmation that
      // their toggle actually reached (or left) the storefront so they
      // don't have to hop over to the shop to verify.
      if (payload.status === 'published') {
        toast.success('Product published to shop');
      } else if (payload.status === 'draft' && currentStatus === 'published') {
        toast.success('Product removed from shop');
      } else {
        toast.success('Product updated');
      }
      onSaved();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="product-editor-dialog">
        <DialogHeader>
          <DialogTitle>{product.title || 'Product'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 cursor-pointer">
            <Checkbox
              checked={purchasable}
              onCheckedChange={(c) => setPurchasable(!!c)}
              data-testid="editor-purchasable"
            />
            <span className="text-sm font-medium text-slate-800">List in shop (publish now)</span>
            <span className="ml-auto text-[11px] text-slate-500">
              Check to publish, uncheck to unpublish.
            </span>
          </label>

          <div>
            <Label className="text-xs uppercase text-slate-500">
              Unit price (fallback when no tier matches)
            </Label>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-slate-500">$</span>
              <Input
                type="number" step="0.01" min="0"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                placeholder="25.00"
                data-testid="editor-unit-price"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase text-slate-500 flex items-center gap-1.5">
                <Layers size={12} /> Pricing tiers (exact quantity match)
              </Label>
              <Button size="sm" variant="outline" onClick={addTier} data-testid="editor-add-tier">
                <Plus size={12} className="mr-1" /> Add tier
              </Button>
            </div>
            {tiers.length === 0 ? (
              <p className="text-xs text-slate-400 italic">
                No tiers yet — customers will always pay unit price × quantity.
              </p>
            ) : (
              <div className="space-y-2">
                {tiers.map((t, idx) => (
                  <div key={idx} className="flex items-center gap-2"
                       data-testid={`editor-tier-${idx}`}>
                    <Input
                      type="number" min="1"
                      value={t.quantity}
                      onChange={(e) => updateTier(idx, 'quantity', e.target.value)}
                      placeholder="Quantity"
                      className="w-28"
                      data-testid={`editor-tier-${idx}-qty`}
                    />
                    <span className="text-slate-400">→</span>
                    <span className="text-slate-500">$</span>
                    <Input
                      type="number" step="0.01" min="0"
                      value={t.price}
                      onChange={(e) => updateTier(idx, 'price', e.target.value)}
                      placeholder="Total price"
                      className="flex-1"
                      data-testid={`editor-tier-${idx}-price`}
                    />
                    <Button size="sm" variant="ghost"
                            onClick={() => removeTier(idx)}
                            data-testid={`editor-tier-${idx}-remove`}>
                      <Trash2 size={12} className="text-rose-500" />
                    </Button>
                  </div>
                ))}
                <p className="text-[11px] text-slate-400 mt-1">
                  Total price for the whole line at that exact quantity. Example: 100 units → $150 total.
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="editor-save">
            {saving ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Save size={13} className="mr-1.5" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Coupons tab ─────────────────────────────────────────────────────

function CouponsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setRows((await shopAdminAPI.listCoupons()).data.items || []); }
    catch (e) { toast.error(getErrorMessage(e, 'Failed to load coupons')); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try { await shopAdminAPI.deleteCoupon(code); await load(); toast.success('Deleted'); }
    catch (e) { toast.error(getErrorMessage(e, 'Failed to delete')); }
  };

  return (
    <Card className="mt-4">
      <CardHeader className="pb-3 flex-row items-center justify-between">
        <CardTitle className="text-base">Coupons ({rows.length})</CardTitle>
        <Button size="sm" onClick={() => setEditing({})} data-testid="new-coupon-btn">
          <Plus size={13} className="mr-1" /> New coupon
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm">No coupons yet.</div>
        ) : (
          <table className="w-full text-xs" data-testid="admin-coupons-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Code</th>
                <th className="text-left px-3 py-2 font-semibold">Type</th>
                <th className="text-left px-3 py-2 font-semibold">Value</th>
                <th className="text-left px-3 py-2 font-semibold">Min Spend</th>
                <th className="text-left px-3 py-2 font-semibold">Uses</th>
                <th className="text-left px-3 py-2 font-semibold">Free Ship</th>
                <th className="text-left px-3 py-2 font-semibold">Active</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code}>
                  <td className="px-3 py-2 font-mono font-semibold">
                    {r.code}
                    {r.scope === 'category' && (
                      <Badge variant="outline" className="ml-1.5 text-[9px] bg-amber-50 text-amber-700 border-amber-300 normal-case" title={(r.categories || []).join(', ')}>
                        {(r.categories || []).length} cat
                      </Badge>
                    )}
                    {r.max_discount ? <span className="ml-1 text-[9px] text-slate-400">≤${Number(r.max_discount).toFixed(0)}</span> : null}
                  </td>
                  <td className="px-3 py-2">{r.type}</td>
                  <td className="px-3 py-2 font-mono">
                    {r.type === 'percent' ? `${r.value}%` : `$${Number(r.value).toFixed(2)}`}
                  </td>
                  <td className="px-3 py-2 font-mono">${Number(r.min_spend || 0).toFixed(2)}</td>
                  <td className="px-3 py-2">{r.uses || 0}{r.max_uses ? ` / ${r.max_uses}` : ''}</td>
                  <td className="px-3 py-2">{r.free_shipping ? '✓' : '—'}</td>
                  <td className="px-3 py-2">{r.is_active ? '✓' : '✗'}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(r)}
                             data-testid={`edit-coupon-${r.code}`}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r.code)}
                             data-testid={`del-coupon-${r.code}`}>
                      <Trash2 size={12} className="text-rose-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
      {editing !== null && (
        <CouponDialog coupon={editing} onClose={() => setEditing(null)}
                       onSaved={() => { setEditing(null); load(); }} />
      )}
    </Card>
  );
}

function CouponDialog({ coupon, onClose, onSaved }) {
  const editing = !!coupon?.code;
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    code: coupon.code || '', type: coupon.type || 'amount', value: coupon.value ?? '',
    min_spend: coupon.min_spend ?? '', max_uses: coupon.max_uses ?? '',
    max_discount: coupon.max_discount ?? '',
    expires_at: coupon.expires_at ? coupon.expires_at.slice(0, 10) : '',
    free_shipping: !!coupon.free_shipping, is_active: coupon.is_active !== false,
    description: coupon.description || '',
    scope: coupon.scope || 'order',
    categories: coupon.categories || [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    shopAdminAPI.listShopCategories().then((r) => setCategories(r.data.items || [])).catch(() => setCategories([]));
  }, []);

  const toggleCat = (cat) => setForm((f) => ({
    ...f,
    categories: f.categories.includes(cat) ? f.categories.filter((c) => c !== cat) : [...f.categories, cat],
  }));

  const save = async () => {
    if (form.scope === 'category' && form.categories.length === 0) {
      toast.error('Select at least one category');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        value: parseFloat(form.value),
        min_spend: form.min_spend === '' ? null : parseFloat(form.min_spend),
        max_uses: form.max_uses === '' ? null : parseInt(form.max_uses, 10),
        max_discount: form.max_discount === '' ? null : parseFloat(form.max_discount),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        categories: form.scope === 'category' ? form.categories : [],
      };
      if (editing) await shopAdminAPI.updateCoupon(coupon.code, payload);
      else         await shopAdminAPI.createCoupon(payload);
      toast.success(editing ? 'Coupon updated' : 'Coupon created');
      onSaved();
    } catch (e) { toast.error(getErrorMessage(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" data-testid="coupon-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${coupon.code}` : 'New coupon'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2">
            <Label className="text-xs">Code *</Label>
            <Input value={form.code} disabled={editing}
                   onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                   data-testid="coupon-code" />
          </div>
          <div>
            <Label className="text-xs">Type *</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="coupon-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="amount">$ off</SelectItem>
                <SelectItem value="percent">% off</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Value *</Label>
            <Input type="number" step="0.01" min="0" value={form.value}
                   onChange={(e) => setForm({ ...form, value: e.target.value })}
                   data-testid="coupon-value" />
          </div>

          <div className="col-span-2">
            <Label className="text-xs">Applies to</Label>
            <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
              <SelectTrigger data-testid="coupon-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="order">Entire order</SelectItem>
                <SelectItem value="category">Specific categories</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.scope === 'category' && (
            <div className="col-span-2" data-testid="coupon-categories">
              <Label className="text-xs">Categories *</Label>
              <div className="mt-1 border rounded-lg p-2 grid grid-cols-2 gap-1.5 max-h-40 overflow-y-auto">
                {categories.length === 0 && <p className="text-xs text-slate-400 col-span-2">No categories found.</p>}
                {categories.map((cat) => (
                  <label key={cat} className="text-xs flex items-center gap-2 cursor-pointer" data-testid={`coupon-cat-${cat}`}>
                    <Checkbox checked={form.categories.includes(cat)} onCheckedChange={() => toggleCat(cat)} />
                    <span className="capitalize">{String(cat).replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">The discount applies only to items in these categories.</p>
            </div>
          )}

          <div>
            <Label className="text-xs">Min spend</Label>
            <Input type="number" step="0.01" min="0" value={form.min_spend}
                   onChange={(e) => setForm({ ...form, min_spend: e.target.value })}
                   placeholder="0 = none" />
          </div>
          <div>
            <Label className="text-xs">Max discount ($)</Label>
            <Input type="number" step="0.01" min="0" value={form.max_discount}
                   onChange={(e) => setForm({ ...form, max_discount: e.target.value })}
                   placeholder="empty = no cap" data-testid="coupon-max-discount" />
          </div>
          <div>
            <Label className="text-xs">Max uses</Label>
            <Input type="number" min="0" value={form.max_uses}
                   onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                   placeholder="empty = ∞" />
          </div>
          <div>
            <Label className="text-xs">Expires (date)</Label>
            <Input type="date" value={form.expires_at}
                   onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </div>
          <div className="col-span-2 flex gap-4 items-center pt-1">
            <label className="text-xs flex items-center gap-2">
              <Checkbox checked={form.free_shipping}
                         onCheckedChange={(c) => setForm({ ...form, free_shipping: !!c })} />
              Free shipping
            </label>
            <label className="text-xs flex items-center gap-2">
              <Checkbox checked={form.is_active}
                         onCheckedChange={(c) => setForm({ ...form, is_active: !!c })} />
              Active
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.code || !form.value}
                  data-testid="coupon-save">
            {saving && <Loader2 size={13} className="mr-1 animate-spin" />}
            {editing ? 'Save' : 'Create'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Shipping tab ────────────────────────────────────────────────────

function ShippingTab() {
  const [settings, setSettings] = useState({ flat_shipping: 0, free_shipping_over: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    shopAdminAPI.getSettings().then((r) => setSettings(r.data))
      .catch((e) => toast.error(getErrorMessage(e, 'Failed to load settings')))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await shopAdminAPI.updateSettings({
        flat_shipping: parseFloat(settings.flat_shipping) || 0,
        free_shipping_over: parseFloat(settings.free_shipping_over) || 0,
      });
      setSettings(r.data);
      toast.success('Shipping settings saved');
    } catch (e) { toast.error(getErrorMessage(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <Card className="mt-4 max-w-xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Shipping rules</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-xs">Flat shipping rate ($)</Label>
          <Input type="number" step="0.01" min="0" value={settings.flat_shipping}
                 onChange={(e) => setSettings({ ...settings, flat_shipping: e.target.value })}
                 data-testid="flat-shipping-input" />
          <p className="text-[11px] text-slate-500 mt-1">
            Applied to every cart. Set to 0 for free shipping by default.
          </p>
        </div>
        <div>
          <Label className="text-xs">Free shipping over ($)</Label>
          <Input type="number" step="0.01" min="0" value={settings.free_shipping_over}
                 onChange={(e) => setSettings({ ...settings, free_shipping_over: e.target.value })}
                 data-testid="free-shipping-input" />
          <p className="text-[11px] text-slate-500 mt-1">
            Waives flat rate when the cart subtotal (after discount) meets this threshold. Set to 0 to disable.
          </p>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-shipping-btn">
            {saving && <Loader2 size={13} className="mr-1 animate-spin" />}
            <Save size={13} className="mr-1" />
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


// ─── Orders tab (Phase 27.56) ─────────────────────────────────────────

const PROVIDER_LABEL = { quickbooks: 'Credit Card', paypal: 'PayPal', afterpay: 'Afterpay', klarna: 'Klarna' };
const METHOD_LABEL = { card: 'Card', paypal: 'PayPal', paypal_paylater: 'PayPal Pay Later', afterpay: 'Afterpay', klarna: 'Klarna' };
const PAY_STATUS_TONE = {
  paid: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  pending: 'bg-amber-50 text-amber-800 border-amber-300',
  processing: 'bg-sky-50 text-sky-800 border-sky-300',
  failed: 'bg-rose-50 text-rose-800 border-rose-300',
  cancelled: 'bg-slate-100 text-slate-700 border-slate-300',
  partially_refunded: 'bg-orange-50 text-orange-800 border-orange-300',
  refunded: 'bg-purple-50 text-purple-800 border-purple-300',
};

export function OrdersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerFilter, setProviderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [detail, setDetail] = useState(null); // { order, events }
  const [form, setForm] = useState(null);      // editable copy of order
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [refundAmt, setRefundAmt] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [showSpecial, setShowSpecial] = useState(false);
  const [savingSpecial, setSavingSpecial] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await shopAdminAPI.listOrders({
        limit: 100,
        provider: providerFilter || undefined,
        status: statusFilter || undefined,
      });
      setRows(r.data.orders || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load orders'));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [providerFilter, statusFilter]);

  const buildForm = (o) => ({
    items: (o.items || []).map((i) => ({
      title: i.title || '',
      quantity: Number(i.quantity || 0),
      unit_price: Number(i.unit_price || 0),
      product_id: i.product_id, variant_id: i.variant_id, sku: i.sku,
    })),
    shipping_amount: Number(o.shipping_amount || 0),
    discount_amount: Number(o.discount_amount || 0),
    payment_status: o.payment_status || o.status || 'pending',
    fulfillment_status: o.fulfillment_status || 'unfulfilled',
    tracking_number: o.tracking_number || '',
    shipping_carrier: o.shipping_carrier || '',
    customer_name: o.customer_name || '',
    customer_email: o.customer_email || '',
    customer_phone: o.customer_phone || '',
    shipping_address: {
      line1: o.shipping_address?.line1 || '',
      line2: o.shipping_address?.line2 || '',
      city: o.shipping_address?.city || '',
      state: o.shipping_address?.state || '',
      zip: o.shipping_address?.zip || '',
    },
    admin_notes: o.admin_notes || '',
  });

  const openDetail = async (orderId) => {
    try {
      const r = await shopAdminAPI.getOrder(orderId);
      setDetail(r.data);
      setForm(buildForm(r.data.order));
      const remaining = Math.max(0, Number(r.data.order.total || 0) - Number(r.data.order.refund_amount || 0));
      setRefundAmt(remaining.toFixed(2));
      setRefundReason('');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load order'));
    }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updAddr = (k, v) => setForm((f) => ({ ...f, shipping_address: { ...f.shipping_address, [k]: v } }));
  const updItem = (idx, k, v) => setForm((f) => ({
    ...f, items: f.items.map((it, i) => i === idx ? { ...it, [k]: v } : it),
  }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { title: '', quantity: 1, unit_price: 0 }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const computedSubtotal = form ? form.items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0) : 0;
  const computedTotal = form ? computedSubtotal + Number(form.shipping_amount || 0) - Number(form.discount_amount || 0) : 0;

  const saveOrder = async (extra = {}) => {
    if (!detail) return;
    // Warn when reducing the total on a paid order → auto-refund the difference.
    const oldTotal = Number(detail.order.total || 0);
    const already = Number(detail.order.refund_amount || 0);
    const isPaid = ['paid', 'partially_refunded'].includes(detail.order.payment_status);
    const willRefund = Math.min(Math.round((oldTotal - computedTotal) * 100) / 100, Math.round((oldTotal - already) * 100) / 100);
    if (isPaid && willRefund > 0.009 && !extra.payment_status) {
      const ok = window.confirm(`El total baja de $${oldTotal.toFixed(2)} a $${computedTotal.toFixed(2)}.\n\nSe reembolsarán $${willRefund.toFixed(2)} al cliente automáticamente por ${PROVIDER_LABEL[detail.order.payment_provider] || detail.order.payment_provider || 'el proveedor de pago'}.\n\n¿Continuar?`);
      if (!ok) return;
    }
    setSaving(true);
    try {
      const payload = {
        items: form.items.map((it) => ({
          title: it.title,
          quantity: Number(it.quantity || 0),
          unit_price: Number(it.unit_price || 0),
          product_id: it.product_id, variant_id: it.variant_id, sku: it.sku,
        })),
        shipping_amount: Number(form.shipping_amount || 0),
        discount_amount: Number(form.discount_amount || 0),
        payment_status: form.payment_status,
        fulfillment_status: form.fulfillment_status,
        tracking_number: form.tracking_number || undefined,
        shipping_carrier: form.shipping_carrier || undefined,
        shipping_address: form.shipping_address,
        customer_name: form.customer_name || undefined,
        customer_email: form.customer_email || undefined,
        customer_phone: form.customer_phone || undefined,
        admin_notes: form.admin_notes || undefined,
        ...extra,
      };
      const r = await shopAdminAPI.updateOrder(detail.order.order_id, payload);
      if (r.data.auto_refund?.amount > 0) {
        toast.success(`Orden actualizada · Reembolsados $${Number(r.data.auto_refund.amount).toFixed(2)} al cliente`);
      } else {
        toast.success('Order updated');
      }
      setDetail((d) => ({ ...d, order: r.data.order }));
      setForm(buildForm(r.data.order));
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Update failed'));
    } finally { setSaving(false); }
  };

  const quickStatus = async (payment_status) => {
    setForm((f) => ({ ...f, payment_status }));
    await saveOrder({ payment_status });
  };

  const changeSpecialStatus = async (special_status) => {
    if (!detail) return;
    setSavingSpecial(true);
    try {
      const r = await shopAdminAPI.setSpecialStatus(detail.order.order_id, special_status);
      toast.success(`Status updated to ${special_status.replace('_', ' ')}`);
      const dr = await shopAdminAPI.getOrder(detail.order.order_id);
      setDetail(dr.data);
      setForm(buildForm(dr.data.order));
      void r;
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Status update failed'));
    } finally { setSavingSpecial(false); }
  };

  const removeOrder = async () => {
    if (!detail) return;
    if (!window.confirm('Delete this order permanently? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await shopAdminAPI.deleteOrder(detail.order.order_id);
      toast.success('Order deleted');
      setDetail(null); setForm(null);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Delete failed'));
    } finally { setDeleting(false); }
  };

  const doRefund = async () => {
    if (!detail) return;
    const amt = Number(refundAmt);
    if (!(amt > 0)) return toast.error('Enter a valid amount');
    setRefunding(true);
    try {
      const r = await shopAdminAPI.refundOrder(detail.order.order_id, {
        amount: amt,
        reason: refundReason || undefined,
      });
      toast.success(r.data.fully_refunded ? 'Full refund issued' : `Refunded $${amt.toFixed(2)}`);
      setDetail(null); setForm(null);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Refund failed'));
    } finally { setRefunding(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>Storefront Orders ({total})</span>
          <div className="flex gap-2 items-center">
            <Button variant="outline" size="sm" onClick={() => setShowSpecial(true)} data-testid="create-special-order-btn" className="text-[#0a1f47] border-[#0a1f47]/30">
              <Plus size={13} className="mr-1.5" /> Create Special Order
            </Button>
            <Select value={providerFilter || 'all'} onValueChange={(v) => setProviderFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="orders-provider-filter"><SelectValue placeholder="All providers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All providers</SelectItem>
                <SelectItem value="quickbooks">Credit Card</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="afterpay">Afterpay</SelectItem>
                <SelectItem value="klarna">Klarna</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="orders-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="partially_refunded">Partially refunded</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={load} data-testid="orders-refresh-btn">
              <RefreshCw size={13} />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-8 justify-center"><Loader2 size={14} className="animate-spin" /> Loading orders…</div>
        ) : rows.length === 0 ? (
          <div className="text-slate-500 text-sm py-8 text-center">No orders match these filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead className="text-xs text-slate-500 uppercase tracking-wide">
                <tr className="border-b">
                  <th className="text-left py-2">Order</th>
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Method</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Total</th>
                  <th className="text-right py-2">Refunded</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.order_id} className="border-b hover:bg-slate-50" data-testid={`order-row-${o.order_id}`}>
                    <td className="py-2 font-mono text-xs">
                      {o.order_id}
                      {o.is_special_order && (
                        <Badge variant="outline" className="ml-1.5 bg-[#0a1f47]/5 text-[#0a1f47] border-[#0a1f47]/30 text-[10px]">Special</Badge>
                      )}
                    </td>
                    <td className="py-2 text-xs text-slate-500">{o.created_at ? new Date(o.created_at).toLocaleString() : '—'}</td>
                    <td className="py-2 text-xs">
                      <span>{METHOD_LABEL[o.payment_method] || o.payment_method || '—'}</span>
                      <span className="text-slate-400 ml-1">· {PROVIDER_LABEL[o.payment_provider] || o.payment_provider || '—'}</span>
                    </td>
                    <td className="py-2">
                      <Badge variant="outline" className={PAY_STATUS_TONE[o.payment_status] || 'bg-slate-100 text-slate-700 border-slate-300'}>
                        {o.payment_status || o.status || '—'}
                      </Badge>
                    </td>
                    <td className="py-2 text-right font-mono">${Number(o.total || 0).toFixed(2)}</td>
                    <td className="py-2 text-right font-mono text-slate-500">${Number(o.refund_amount || 0).toFixed(2)}</td>
                    <td className="py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openDetail(o.order_id)} data-testid={`order-detail-btn-${o.order_id}`}>Detail</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setForm(null); } }}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="order-detail-dialog">
            <DialogHeader>
              <DialogTitle className="text-base flex items-center gap-2">
                Order <span className="font-mono text-sm">{detail?.order?.order_id}</span>
                {detail && (
                  <Badge variant="outline" className={PAY_STATUS_TONE[detail.order.payment_status] || 'bg-slate-100 text-slate-700 border-slate-300'}>
                    {detail.order.payment_status || detail.order.status || '—'}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="sr-only">Manage this order — edit items, shipping, status, refunds and more.</DialogDescription>
            {detail && form && (
              <div className="space-y-5 text-sm">
                {/* ── Quick actions ── */}
                <div className="flex flex-wrap gap-2">
                  {form.payment_status !== 'paid' && (
                    <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={() => quickStatus('paid')} disabled={saving} data-testid="order-mark-paid-btn">
                      <CheckCircle2 size={13} className="mr-1.5" /> Mark as paid
                    </Button>
                  )}
                  {form.payment_status !== 'cancelled' && (
                    <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => quickStatus('cancelled')} disabled={saving} data-testid="order-cancel-btn">
                      <Ban size={13} className="mr-1.5" /> Cancel order
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-rose-700 border-rose-300 hover:bg-rose-50 ml-auto" onClick={removeOrder} disabled={deleting} data-testid="order-delete-btn">
                    {deleting ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Trash2 size={13} className="mr-1.5" />} Delete
                  </Button>
                </div>

                {/* ── Special order lifecycle ── */}
                {detail.order.is_special_order && (
                  <div className="rounded-lg border border-[#0a1f47]/20 bg-[#0a1f47]/5 p-3 space-y-2" data-testid="special-order-panel">
                    <div className="flex items-center gap-2 text-xs font-semibold text-[#0a1f47]">
                      <Package size={13} /> Special Order — {detail.order.title || 'Untitled'}
                      <span className="text-slate-500 font-normal">· Agent NPN {detail.order.assigned_agent_npn}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs whitespace-nowrap">Lifecycle status</Label>
                      <Select value={detail.order.special_status || 'pending_payment'} onValueChange={changeSpecialStatus} disabled={savingSpecial}>
                        <SelectTrigger className="h-9 w-56" data-testid="special-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['pending_payment', 'paid', 'placed', 'shipped', 'delivered', 'completed', 'cancelled'].map((s) => (
                            <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {savingSpecial && <Loader2 size={14} className="animate-spin text-slate-400" />}
                    </div>
                    <p className="text-[11px] text-slate-500">
                      The assigned agent pays this order from their account. Moving to <strong>paid</strong> here marks it paid manually.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Payment status</Label>
                    <Select value={form.payment_status} onValueChange={(v) => upd('payment_status', v)}>
                      <SelectTrigger className="h-9" data-testid="order-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['pending', 'paid', 'processing', 'failed', 'cancelled', 'partially_refunded', 'refunded'].map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Provider · Method</Label>
                    <div className="h-9 flex items-center text-slate-600">
                      {PROVIDER_LABEL[detail.order.payment_provider] || detail.order.payment_provider || '—'} · {METHOD_LABEL[detail.order.payment_method] || detail.order.payment_method || '—'}
                    </div>
                  </div>
                </div>

                {/* ── Shipping / fulfillment ── */}
                <div className="border-t pt-3 space-y-3">
                  <div className="text-xs font-semibold flex items-center gap-1.5 text-slate-700"><Truck size={13} /> Shipping & fulfillment</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Fulfillment</Label>
                      <Select value={form.fulfillment_status} onValueChange={(v) => upd('fulfillment_status', v)}>
                        <SelectTrigger className="h-9" data-testid="order-fulfillment-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['unfulfilled', 'processing', 'shipped', 'delivered'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Carrier</Label>
                      <Input value={form.shipping_carrier} onChange={(e) => upd('shipping_carrier', e.target.value)} placeholder="UPS, USPS…" data-testid="order-carrier-input" />
                    </div>
                    <div>
                      <Label className="text-xs">Tracking #</Label>
                      <Input value={form.tracking_number} onChange={(e) => upd('tracking_number', e.target.value)} data-testid="order-tracking-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Address line 1</Label><Input value={form.shipping_address.line1} onChange={(e) => updAddr('line1', e.target.value)} data-testid="order-addr-line1" /></div>
                    <div><Label className="text-xs">Address line 2</Label><Input value={form.shipping_address.line2} onChange={(e) => updAddr('line2', e.target.value)} /></div>
                    <div><Label className="text-xs">City</Label><Input value={form.shipping_address.city} onChange={(e) => updAddr('city', e.target.value)} /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs">State</Label><Input value={form.shipping_address.state} onChange={(e) => updAddr('state', e.target.value)} /></div>
                      <div><Label className="text-xs">ZIP</Label><Input value={form.shipping_address.zip} onChange={(e) => updAddr('zip', e.target.value)} /></div>
                    </div>
                  </div>
                </div>

                {/* ── Customer ── */}
                <div className="border-t pt-3 space-y-2">
                  <div className="text-xs font-semibold text-slate-700">Customer</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">Name</Label><Input value={form.customer_name} onChange={(e) => upd('customer_name', e.target.value)} data-testid="order-customer-name" /></div>
                    <div><Label className="text-xs">Email</Label><Input value={form.customer_email} onChange={(e) => upd('customer_email', e.target.value)} /></div>
                    <div><Label className="text-xs">Phone</Label><Input value={formatPhoneInput(form.customer_phone)} onChange={(e) => upd('customer_phone', e.target.value)} /></div>
                  </div>
                </div>

                {/* ── Line items ── */}
                <div className="border-t pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-semibold text-slate-700">Items</div>
                    <Button size="sm" variant="outline" onClick={addItem} data-testid="order-add-item-btn"><Plus size={13} className="mr-1" /> Add item</Button>
                  </div>
                  <div className="space-y-2">
                    {form.items.map((it, idx) => (
                      <div key={idx} className="grid grid-cols-[1fr_70px_90px_80px_auto] gap-2 items-end" data-testid={`order-item-${idx}`}>
                        <div><Label className="text-[10px] text-slate-400">Title</Label><Input value={it.title} onChange={(e) => updItem(idx, 'title', e.target.value)} /></div>
                        <div><Label className="text-[10px] text-slate-400">Qty</Label><Input type="number" value={it.quantity} onChange={(e) => updItem(idx, 'quantity', e.target.value)} data-testid={`order-item-qty-${idx}`} /></div>
                        <div><Label className="text-[10px] text-slate-400">Unit $</Label><Input type="number" value={it.unit_price} onChange={(e) => updItem(idx, 'unit_price', e.target.value)} data-testid={`order-item-price-${idx}`} /></div>
                        <div className="text-right font-mono text-xs pb-2">${(Number(it.quantity || 0) * Number(it.unit_price || 0)).toFixed(2)}</div>
                        <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-rose-600" data-testid={`order-item-remove-${idx}`}><X size={14} /></Button>
                      </div>
                    ))}
                    {form.items.length === 0 && <div className="text-xs text-slate-400">No items.</div>}
                  </div>
                </div>

                {/* ── Amounts ── */}
                <div className="border-t pt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono">${computedSubtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between items-center"><span className="text-slate-500">Shipping</span><Input className="w-24 h-7 text-right font-mono" type="number" value={form.shipping_amount} onChange={(e) => upd('shipping_amount', e.target.value)} data-testid="order-shipping-amount" /></div>
                  <div className="flex justify-between items-center"><span className="text-slate-500">Discount</span><Input className="w-24 h-7 text-right font-mono" type="number" value={form.discount_amount} onChange={(e) => upd('discount_amount', e.target.value)} data-testid="order-discount-amount" /></div>
                  <div className="flex justify-between font-semibold"><span>Total</span><span className="font-mono" data-testid="order-computed-total">${computedTotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-slate-500"><span>Refunded</span><span className="font-mono">${Number(detail.order.refund_amount || 0).toFixed(2)}</span></div>
                </div>

                {/* ── Notes ── */}
                <div className="border-t pt-3">
                  <Label className="text-xs">Internal notes</Label>
                  <Textarea rows={2} value={form.admin_notes} onChange={(e) => upd('admin_notes', e.target.value)} data-testid="order-admin-notes" />
                </div>

                {/* ── Change history / timeline ── */}
                {(detail.events || []).length > 0 && (
                  <div className="border-t pt-3">
                    <div className="text-slate-500 text-xs mb-1.5 font-semibold">Change history</div>
                    <ul className="text-xs space-y-2" data-testid="order-history">
                      {[...detail.events].reverse().map((e, idx) => {
                        const p = e.payload || {};
                        const who = p.by || 'system';
                        const label = e.event === 'admin.order_updated' ? 'Edited' : e.event.replace(/[._]/g, ' ');
                        return (
                          <li key={idx} className="border-l-2 border-slate-200 pl-2">
                            <div className="flex justify-between text-slate-600">
                              <span className="font-medium">{label}{p.by ? ` · ${who}` : ''}</span>
                              <span className="font-mono text-slate-400">{new Date(e.created_at).toLocaleString()}</span>
                            </div>
                            {(p.changes || []).map((c, ci) => (
                              <div key={ci} className="text-slate-500 ml-1">
                                {c.field}: <span className="line-through text-slate-400">{String(c.from ?? '—')}</span> → <span className="text-slate-700">{String(c.to ?? '—')}</span>
                              </div>
                            ))}
                            {p.auto_refund?.amount > 0 && (
                              <div className="text-amber-700 ml-1">Auto-refund: ${Number(p.auto_refund.amount).toFixed(2)}</div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* ── Refund (only for paid) ── */}
                {['paid', 'partially_refunded'].includes(detail.order.payment_status) && (
                  <div className="border-t pt-3 space-y-2">
                    <div className="text-xs font-semibold text-slate-700">Issue refund</div>
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                      <div>
                        <Label className="text-xs">Amount (USD)</Label>
                        <Input value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} data-testid="refund-amount" />
                      </div>
                      <div>
                        <Label className="text-xs">Reason (optional)</Label>
                        <Input value={refundReason} onChange={(e) => setRefundReason(e.target.value)} data-testid="refund-reason" />
                      </div>
                      <Button variant="outline" onClick={doRefund} disabled={refunding} data-testid="refund-submit-btn">
                        {refunding && <Loader2 size={13} className="mr-1.5 animate-spin" />}
                        Refund
                      </Button>
                    </div>
                  </div>
                )}

                {/* ── Save ── */}
                <div className="border-t pt-3 flex justify-end gap-2 sticky bottom-0 bg-white">
                  <Button variant="outline" onClick={() => { setDetail(null); setForm(null); }}>Close</Button>
                  <Button onClick={() => saveOrder()} disabled={saving} data-testid="order-save-btn">
                    {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
                    Save changes
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <CreateSpecialOrderDialog
          open={showSpecial}
          onClose={() => setShowSpecial(false)}
          onCreated={() => { setShowSpecial(false); load(); }}
        />
      </CardContent>
    </Card>
  );
}

// ─── Create Special Order dialog ──────────────────────────────────────
function CreateSpecialOrderDialog({ open, onClose, onCreated }) {
  const [agents, setAgents] = useState([]);
  const [npn, setNpn] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [shipping, setShipping] = useState(0);
  const [notify, setNotify] = useState(true);
  const [items, setItems] = useState([{ title: '', quantity: 1, unit_price: 0 }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      agentAPI.simpleList().then((r) => {
        const seen = new Set();
        const unique = (r.data || []).filter((a) => {
          if (!a.npn || seen.has(a.npn)) return false;
          seen.add(a.npn); return true;
        });
        setAgents(unique);
      }).catch(() => setAgents([]));
      setNpn(''); setTitle(''); setNotes(''); setShipping(0); setNotify(true);
      setItems([{ title: '', quantity: 1, unit_price: 0 }]);
    }
  }, [open]);

  const updItem = (idx, k, v) => setItems((its) => its.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const addItem = () => setItems((its) => [...its, { title: '', quantity: 1, unit_price: 0 }]);
  const removeItem = (idx) => setItems((its) => its.filter((_, i) => i !== idx));

  const subtotal = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
  const total = subtotal + Number(shipping || 0);

  const submit = async () => {
    if (!npn) return toast.error('Select an agent');
    const cleanItems = items
      .filter((it) => (it.title || '').trim() && Number(it.quantity) > 0)
      .map((it) => ({ title: it.title.trim(), quantity: Number(it.quantity), unit_price: Number(it.unit_price || 0) }));
    if (cleanItems.length === 0) return toast.error('Add at least one item with a title and quantity');
    setSaving(true);
    try {
      const r = await shopAdminAPI.createSpecialOrder({
        agent_npn: npn,
        title: title || undefined,
        items: cleanItems,
        shipping_amount: Number(shipping || 0),
        admin_notes: notes || undefined,
        notify,
      });
      toast.success(r.data.emailed ? 'Special order created — agent emailed a pay link' : 'Special order created');
      onCreated();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not create special order'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="create-special-order-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><Package size={16} /> Create Special Order</DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">Create a custom order and assign it to an agent to pay.</DialogDescription>
        <div className="space-y-4 text-sm">
          <div>
            <Label className="text-xs">Assign to agent *</Label>
            <Select value={npn} onValueChange={setNpn}>
              <SelectTrigger className="h-9" data-testid="special-agent-select"><SelectValue placeholder="Select an agent…" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {agents.map((a) => (
                  <SelectItem key={a.npn} value={a.npn}>{a.name} · {a.npn}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Order title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Custom banner set" data-testid="special-title-input" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Items *</Label>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="special-add-item-btn"><Plus size={13} className="mr-1" /> Add item</Button>
            </div>
            {items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_60px_80px_auto] gap-2 items-end" data-testid={`special-item-${idx}`}>
                <div><Label className="text-[10px] text-slate-400">Title</Label><Input value={it.title} onChange={(e) => updItem(idx, 'title', e.target.value)} data-testid={`special-item-title-${idx}`} /></div>
                <div><Label className="text-[10px] text-slate-400">Qty</Label><Input type="number" value={it.quantity} onChange={(e) => updItem(idx, 'quantity', e.target.value)} data-testid={`special-item-qty-${idx}`} /></div>
                <div><Label className="text-[10px] text-slate-400">Unit $</Label><Input type="number" value={it.unit_price} onChange={(e) => updItem(idx, 'unit_price', e.target.value)} data-testid={`special-item-price-${idx}`} /></div>
                <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-rose-600"><X size={14} /></Button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Shipping (USD)</Label>
              <Input type="number" value={shipping} onChange={(e) => setShipping(e.target.value)} data-testid="special-shipping-input" />
            </div>
            <div className="flex items-end justify-end font-semibold">
              <span className="text-slate-500 mr-2 font-normal">Total</span>
              <span className="font-mono" data-testid="special-total">${total.toFixed(2)}</span>
            </div>
          </div>
          <div>
            <Label className="text-xs">Internal notes</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="special-notes-input" />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="special-notify-checkbox" />
            Email the agent a pay link now
          </label>
          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="special-create-submit-btn">
              {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
              Create &amp; assign
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
